import {
  type CreateOneRequestType,
  type GetListResponseType,
  type GetOneRequestType,
  type UpdateOneRequestType,
  type ResourceAdapterType,
  type ResourceAdapterToAdapterQueryKeysType,
  fetchAndCollate,
  getFilterObject,
  Resources,
  type GetOneResponseType,
  type GetListRequestType,
  type CreateOneResponseType,
  type UpdateOneResponseType,
  type DeleteOneRequestType,
  type DeleteOneResponseType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";
import {
  type EventSchemaGetListRequestType,
  type EventSchemaType,
} from "../schemas/EventSchemaSchemas.ts";

export interface EventSchemaAdapterType
  extends Pick<
    ResourceAdapterType<EventSchemaType>,
    "getOne" | "getList" | "createOne" | "updateOne" | "deleteOne"
  > {
  getOne: (
    params: GetOneRequestType
  ) => Promise<GetOneResponseType<EventSchemaType>>;
  getList: (
    params: GetListRequestType
  ) => Promise<GetListResponseType<EventSchemaType>>;
  createOne: (
    params: CreateOneRequestType<EventSchemaType>
  ) => Promise<CreateOneResponseType<EventSchemaType>>;
  updateOne: (
    params: UpdateOneRequestType<EventSchemaType>
  ) => Promise<UpdateOneResponseType<EventSchemaType>>;
  deleteOne: (
    params: DeleteOneRequestType<EventSchemaType>
  ) => Promise<DeleteOneResponseType>;
}

const endpoint = "/event_schemas";

export const EventSchemaAdapter: EventSchemaAdapterType = {
  /**
   * Get Event Schema
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_schemas/get_event_schema
   */
  getOne: ({ id }: GetOneRequestType) =>
    restAPI.nodeAPI
      .GET<EventSchemaType>({ endpoint: `${endpoint}/${id}` })
      .then((data) => ({ data })),

  /**
   * List Event Schemas
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_schemas/list_event_schemas
   */
  getList: ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    filters,
  }: EventSchemaGetListRequestType) =>
    fetchAndCollate<EventSchemaType>(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<EventSchemaType>>({
          endpoint: `${endpoint}/search`,
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),

  /**
   * Create Event Schema
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_schemas/create_event_schema
   */
  createOne: ({ data }: CreateOneRequestType<EventSchemaType>) =>
    restAPI.nodeAPI
      .POST<EventSchemaType>({ endpoint, body: data })
      .then((data) => ({ data })),

  /**
   * Update Event Schema
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_schemas/update_event_schema
   */
  updateOne: ({ id, data }: UpdateOneRequestType<EventSchemaType>) =>
    restAPI.nodeAPI
      .PUT<EventSchemaType>({ endpoint: `${endpoint}/${id}`, body: data })
      .then((data) => ({ data })),

  /**
   * Delete Event Schema
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_schemas/delete_event_schema
   */
  deleteOne: ({ id }: { id: string }) =>
    restAPI.nodeAPI.DELETE({ endpoint: `${endpoint}/${id}` }),
};

export const EventSchemaAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<EventSchemaAdapterType> =
  {
    getList: (params) => [Resources.EVENT_SCHEMA, "getList", params] as const,
    getOne: (params) => [Resources.EVENT_SCHEMA, "getOne", params.id] as const,
    updateOne: () => [Resources.EVENT_SCHEMA] as const,
    createOne: () => [Resources.EVENT_SCHEMA] as const,
    deleteOne: () => [Resources.EVENT_SCHEMA] as const,
  };
